




























































































































































import {
  CellType,
  EBlockWidth,
  NodeType,
  RowType,
  LinkBlockHelper,
  SlideShowHelper,
  Context,
} from '@getaccept/editor-lib';
import type { Node, MergeValues } from '@getaccept/editor-lib';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import { ObserveVisibility } from 'vue-observe-visibility';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { PropType } from '@vue/composition-api';
import { defineComponent, computed } from '@vue/composition-api';
import type { PusherClient } from '@getaccept/pusher';
import type { Comment } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import EditorNodeVideo from './EditorNodeVideo.vue';
import EditorNodePricingTable from './pricing-table/EditorNodePricingTable.vue';
import EditorNodeText from './EditorNodeText.vue';
import EditorNodeTable from './EditorNodeTable.vue';
import EditorNodeImage from './EditorNodeImage.vue';
import EditorNodeForm from './EditorNodeForm.vue';
import EditorNodeDivider from './EditorNodeDivider.vue';
import EditorNodeLink from './EditorNodeLink.vue';
import EditorNodeSignature from './EditorNodeSignature.vue';
import EditorNodeContractLink from './contract-link/EditorNodeContractLink.vue';

export default defineComponent({
  name: 'EditorCell',
  components: {
    EditorNodeDivider,
    EditorNodeImage,
    EditorNodeTable,
    EditorNodeText,
    EditorNodeLink,
    EditorNodeVideo,
    EditorNodeForm,
    EditorNodePricingTable,
    EditorNodeSignature,
    EditorNodeContractLink,
  },
  directives: {
    'observe-visibility': ObserveVisibility,
  },
  props: {
    context: { type: String as PropType<Context>, default: () => Context.Document },
    nodes: { type: Array as PropType<Node[]>, default: () => [] },
    mergeValues: {
      type: Object as PropType<MergeValues>,
      default: () => ({ document: {}, sender: {}, recipients: [], entity: {} }),
    },
    isPdfViewer: { type: Boolean },
    rowType: { type: String as PropType<RowType>, default: null },
    parentFirstChild: { type: Boolean },
    parentLastChild: { type: Boolean },
    blockWidthClass: {
      type: String as PropType<EBlockWidth>,
      default: null,
    },
    focusedRequiredId: { type: String, default: '' },
    cellWidth: { type: Number, default: 100 },
    pageWidth: { type: Number, default: 0 },
    isBlockToggled: { type: Boolean },
    cellType: {
      type: String as PropType<CellType>,
      default: null,
    },
    isSigningSite: { type: Boolean },
    sender: {
      type: Object as PropType<User>,
      default: () => ({}),
    },
    recipient: {
      type: Object as PropType<Recipient>,
      default: () => ({}),
    },
    recipientInput: { type: Array as PropType<EditorRecipientInput[]>, default: () => [] },
    updatingInputs: { type: Array as PropType<EditorRecipientInput[]>, default: () => [] },
    failedInputUpdates: { type: Array as PropType<EditorRecipientInput[]>, default: () => [] },
    theme: {
      type: Object as PropType<SigningTheme>,
      default: () => ({}),
    },
    currentSlide: { type: Number, default: 0 },
    isGridView: { type: Boolean },
    transitionName: { type: String, default: '' },
    partiallySignedFields: { type: Array as PropType<PartiallySignedEvent[]>, default: () => [] },
    pusherClient: { type: Object as PropType<PusherClient>, default: undefined },
    comments: { type: Array as PropType<Comment[]>, default: () => [] },
    selectedCommentId: { type: String, default: '' },
    hasContextualCommenting: { type: Boolean, default: false },
  },
  setup(props) {
    const filterNodes = ({ type }: Node) => {
      switch (props.cellType) {
        case CellType.ImageAndText:
          return [NodeType.Image, NodeType.Text].includes(type);
        case CellType.ImageOnly:
          return NodeType.Image === type;
        case CellType.TextOnly:
          return NodeType.Text === type;
        default:
          return false;
      }
    };

    const filteredNodes = computed(() =>
      props.cellType ? props.nodes.filter(filterNodes) : props.nodes
    );

    const isSmallScreen = computed(
      () =>
        props.blockWidthClass === EBlockWidth.XXSmall ||
        props.blockWidthClass === EBlockWidth.XSmall
    );

    const widthStyle = computed(() => {
      if (isSmallScreen.value) {
        return 'width: 100%;';
      } else if (props.rowType === RowType.Columns) {
        return `width:calc(${props.cellWidth}% - 1rem)`;
      }
      return `width:${props.cellWidth}%`;
    });

    const filterComments = (comments: Comment[], nodeId: string) =>
      comments.filter(comment => comment.editorSelection.nodeId === nodeId);

    return {
      filterNodes,
      filteredNodes,
      isSmallScreen,
      widthStyle,
      NodeType,
      RowType,
      hasLinkNode: LinkBlockHelper.hasLinkNode,
      getVisibleSlides: SlideShowHelper.getVisibleSlides,
      filterComments,
    };
  },
});
